import axios, { AxiosResponse } from 'axios'
import ApiService from './api.service'

interface DesignFile {
  id: string
  name: string
  originalName: string
  size: number
  type: string
  uploadDate: string
  uploadedBy: string
  projectId: string
  url?: string
  version: number
}

interface DesignFileUploadResponse {
  success: boolean
  designFile: DesignFile
  message: string
}

interface DesignFileDeleteResponse {
  success: boolean
  deletedFileId: string
  message: string
}

export interface DesignCreationResponse {
  success: boolean
  design: DesignFile
  message: string
}

class DesignService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Document Design Error'
      }
    })
  }
  
  /**
   * Get design files for a specific project
   * @param projectId - The ID of the project
   * @returns Promise that resolves with the project design files
   */
  async getProjectDesignFiles(projectId: string): Promise<DesignFile[]> {
    if (!projectId) {
      return Promise.reject({ message: 'No project ID provided' })
    }
    
    const response = await this.get(`/api/project/${projectId}/design`)
    return response as DesignFile[]
  }

  /**
   * Upload design files to a project
   * @param projectId - The ID of the project
   * @param designId - The ID of the design file
   * @param formData - Form data containing the design files
   * @returns Promise that resolves when design files are uploaded
   */
  async uploadProjectDesignFile(
    projectId: string, 
    designId: string, 
    formData: FormData
  ): Promise<DesignFileUploadResponse> {
    if (!projectId || !designId) {
      return Promise.reject({ message: 'No project ID provided or design ID' })
    }
    
    const response = await this.post(`/api/project/${projectId}/design/${designId}/update`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response as DesignFileUploadResponse
  }

  /**
   * Delete a project design file
   * @param projectId - The ID of the project
   * @param designFileId - The ID of the design file to delete
   * @returns Promise that resolves when the design file is deleted
   */
  async deleteProjectDesignFile(
    projectId: string, 
    designFileId: string
  ): Promise<DesignFileDeleteResponse> {
    if (!projectId || !designFileId) {
      return Promise.reject({ message: 'Project ID and design file ID are required' })
    }
    
    const response = await this.delete(`/api/project/${projectId}/design/${designFileId}`)
    return response as DesignFileDeleteResponse
  }

  /**
   * Download a project design file
   * @param projectId - The ID of the project
   * @param designFileId - The ID of the design file to download
   * @returns Promise that resolves with the design file blob URL
   */
  async downloadDesignFile(projectId: string, designFileId: string): Promise<string> {
    if (!projectId || !designFileId) {
      return Promise.reject({ message: 'Project ID and design file ID are required' })
    }
    
    try {
      // For binary data, we need to use axios directly to get the correct response handling
      const response: AxiosResponse<Blob> = await axios({
        url: `/api/project/${projectId}/design/${designFileId}`,
        method: 'GET',
        responseType: 'blob', // Important: responseType must be blob
        headers: this.getHeaders()
      })
      
      // Use the response.data directly - it's already a blob when responseType is 'blob'
      const blob = response.data  // response.data is already a blob when responseType is 'blob'
      const url = window.URL.createObjectURL(blob)
      return url
      
    } catch (error) {
      console.error('Error downloading design file:', error)
      return Promise.reject({ 
        message: error instanceof Error ? error.message : 'Error downloading design file', 
        originalError: error 
      })
    }
  }

  /**
   * Create a new design entry without file upload
   * @param projectId - The ID of the project
   * @param designName - The name of the design
   * @returns Promise that resolves when design is created
   */
  async createDesign(projectId: string, designName: string): Promise<DesignCreationResponse> {
    if (!projectId || !designName) {
      return Promise.reject({ message: 'Project ID and design are required' })
    }

    const formData = new FormData()
    formData.append('designName', designName)

    const response = await this.post(`/api/project/${projectId}/design/create`, formData)
    return response as DesignCreationResponse
  }
}

// Create and export a singleton instance
export default new DesignService()
